import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AddonSuggestion {
  code: string;
  description: string;
  source: 'template' | 'cpt_relationship' | 'historical';
  /** Label shown to user explaining why this is suggested */
  reason: string;
  /** Sort priority (lower = higher priority) */
  priority: number;
}

interface UseAddonSuggestionsArgs {
  primaryCodes: string[];
  existingCodes: Set<string>;
  templateAddons: { code: string; description: string }[];
  surgeonId?: string;
}

export function useAddonSuggestions({
  primaryCodes,
  existingCodes,
  templateAddons,
  surgeonId,
}: UseAddonSuggestionsArgs) {
  const [relationshipAddons, setRelationshipAddons] = useState<AddonSuggestion[]>([]);
  const [historicalAddons, setHistoricalAddons] = useState<AddonSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch CPT relationship add-ons and historical usage in parallel
  useEffect(() => {
    if (primaryCodes.length === 0) {
      setRelationshipAddons([]);
      setHistoricalAddons([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetchAll = async () => {
      const promises: Promise<void>[] = [];

      // 1. CPT add-on relationships
      promises.push(
        (async () => {
          const { data } = await supabase
            .from('cpt_addon_relationships')
            .select('addon_code, description, relationship_type')
            .in('parent_code', primaryCodes);

          if (!cancelled && data) {
            // Look up descriptions from cpt_codes for any missing
            const codes = data.map(d => d.addon_code);
            const { data: cptData } = await supabase
              .from('cpt_catalog')
              .select('cpt, descriptor, category')
              .in('cpt', codes);

            const cptMap = new Map((cptData || []).map(c => [c.cpt, { code: c.cpt, description: c.descriptor }]));

            setRelationshipAddons(
              data.map(d => ({
                code: d.addon_code,
                description: d.description || cptMap.get(d.addon_code)?.description || d.addon_code,
                source: 'cpt_relationship' as const,
                reason: d.relationship_type === 'add_on' ? 'CPT add-on code' : 'Commonly paired',
                priority: 2,
              }))
            );
          }
        })()
      );

      // 2. Historical surgeon usage
      if (surgeonId) {
        promises.push(
          (async () => {
            const { data } = await supabase
              .from('surgeon_cpt_usage_history')
              .select('secondary_code, usage_count')
              .eq('surgeon_id', surgeonId)
              .in('primary_code', primaryCodes)
              .gte('usage_count', 2)
              .order('usage_count', { ascending: false })
              .limit(10);

            if (!cancelled && data && data.length > 0) {
              const codes = data.map(d => d.secondary_code);
              const { data: cptData } = await supabase
                .from('cpt_catalog')
                .select('cpt, descriptor')
                .in('cpt', codes);

              const cptMap = new Map((cptData || []).map(c => [c.cpt, { code: c.cpt, description: c.descriptor }]));

              setHistoricalAddons(
                data.map(d => ({
                  code: d.secondary_code,
                  description: cptMap.get(d.secondary_code)?.description || d.secondary_code,
                  source: 'historical' as const,
                  reason: `Used ${d.usage_count}× previously`,
                  priority: 3,
                }))
              );
            }
          })()
        );
      }

      await Promise.all(promises);
      if (!cancelled) setIsLoading(false);
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [primaryCodes.join(','), surgeonId]);

  // Merge all sources, deduplicate, exclude already-added codes
  const suggestions = useMemo(() => {
    const map = new Map<string, AddonSuggestion>();

    // Template addons (highest priority)
    for (const a of templateAddons) {
      if (!existingCodes.has(a.code)) {
        map.set(a.code, {
          code: a.code,
          description: a.description,
          source: 'template',
          reason: 'From template',
          priority: 1,
        });
      }
    }

    // CPT relationships
    for (const a of relationshipAddons) {
      if (!existingCodes.has(a.code) && !map.has(a.code)) {
        map.set(a.code, a);
      }
    }

    // Historical
    for (const a of historicalAddons) {
      if (!existingCodes.has(a.code) && !map.has(a.code)) {
        map.set(a.code, a);
      }
    }

    return Array.from(map.values()).sort((a, b) => a.priority - b.priority);
  }, [templateAddons, relationshipAddons, historicalAddons, existingCodes]);

  return { suggestions, isLoading };
}

/**
 * Record that a surgeon used a secondary code alongside a primary code.
 * Uses upsert to increment usage_count.
 */
export async function recordCptUsage(surgeonId: string, primaryCode: string, secondaryCode: string) {
  const { data: existing } = await supabase
    .from('surgeon_cpt_usage_history')
    .select('id, usage_count')
    .eq('surgeon_id', surgeonId)
    .eq('primary_code', primaryCode)
    .eq('secondary_code', secondaryCode)
    .maybeSingle();

  if (existing) {
    await supabase
      .from('surgeon_cpt_usage_history')
      .update({ usage_count: existing.usage_count + 1, last_used_at: new Date().toISOString() })
      .eq('id', existing.id);
  } else {
    await supabase
      .from('surgeon_cpt_usage_history')
      .insert({ surgeon_id: surgeonId, primary_code: primaryCode, secondary_code: secondaryCode });
  }
}
